import { FORM_ERROR } from 'final-form'
import { FormApi } from 'final-form'
import React from 'react'

import Statuses from '@cinch/oxd-utils/request-status/src'
import { WbacService } from '@cinch/oxd-utils/requests/src'
import * as Storage from '@cinch/oxd-utils/storage/src'

import SEO from '../../../../../apps/www/src/components/seo'
import JourneyBuilder from '../../components/journey/journey.component'
import Steps from '../../components/steps'
import WbacWrapper from '../../components/wrapper/wrapper.component'
import { Journey } from '../../types'
import getAllDependants, { getQuestionFromJourney } from './get-all-dependants'
import getDependentParameterName from './get-dependent-question-param'
import useJourney from './get-journey.hook'

import * as styles from './manual-lookup.module.css'

/**
 * This is the expected form generated by the manual lookup
 */
export interface FormData {
  BookId: string // "1"
  Colour: string // "black"
  Derivative: string // "bM4S20LUX2CPTA"
  FuelType: string // "p"
  Manufacturer: string // "bmw"
  MetallicPaint: string // "1"
  Model: string // "4 SERIES"
  RegYear: string // "2014/14"
}

const ManualLookupComponent: React.FC<any> = ({ location, navigate, i18n, path }) => {
  const { token: storageToken } = Storage.get(Storage.StorageKeys.wbac)
  const vrm = location.pathname.split('/').pop()
  const { status, journey, getQuestionOptions, resetFieldOptions, getParentQuestion } = useJourney()
  const hasJourney = journey && status === Statuses.SUCCESS

  const onSubmit = (formData: FormData) => {
    const config = { headers: { Authorization: `JWT ${storageToken}` } }

    return new Promise((resolve) => {
      return WbacService.post(
        '/vehicles/byGuidebook',
        {
          vrm,
          journeyName: 'StandardQuote',
          journeyVariant: 'A',
          colour: formData.Colour,
          guideBookVehicleCode: formData.Derivative,
          regLetter: formData.RegYear,
        },
        config,
      )
        .then(({ data }) => {
          const options = { state: { vehicleInformation: data } }

          navigate('/sell/details', options)
        })
        .catch((e) => resolve({ [FORM_ERROR]: e.message }))
    })
  }

  const onFieldChange = (parameterName: string, form: FormApi) => {
    if (!hasJourney) {
      return {}
    }

    // find the parent question
    const parentQuestion = getParentQuestion(parameterName)

    if (parentQuestion) {
      const fieldState = form.getFieldState(parentQuestion.questionParameterName)

      // if the parent question has no value then we should exit the fn as we shouldn't fetch it's options
      if (!fieldState || !fieldState.value) {
        return
      }
    }

    // try and find a dependent question
    const requestedParameterName = getDependentParameterName(parameterName, journey as Journey)

    // if a dependent question is found, go fetch the available options for it
    if (requestedParameterName) {
      getQuestionOptions({
        requestedParameterName,
        filterParameterDictionary: form.getState().values as any,
      })
    }

    // we need an array of child dependants here so we can modify them
    const dependants = getAllDependants(
      journey!.journeyPages[0].pageQuestions,
      getQuestionFromJourney(parameterName, journey as Journey),
    )

    form.batch(() => {
      dependants.forEach((field: string) => {
        // reset field options array so that they can't select next siblings
        resetFieldOptions(field)

        // reset all possible questions in the dependency tree
        form.change(field, '')

        // set the fields not to be touched, this hides the validation messages
        form.mutators.setFieldTouched(field, false)
      })

      if (form.isValidationPaused()) {
        form.resumeValidation()
      }
    })

    return {}
  }

  return (
    <>
      <SEO noIndex={true} />
      <WbacWrapper>
        <Steps page={path} />
        <section className={styles.main}>
          {hasJourney && (
            <JourneyBuilder
              submitText={i18n.submit}
              onSubmit={onSubmit as any}
              journey={journey}
              onFieldChange={onFieldChange}
            />
          )}
        </section>
      </WbacWrapper>
    </>
  )
}

export default ManualLookupComponent
